import { useMutation, useQuery } from '@tanstack/react-query';
import fetcher, { FleetGetListParams, FleetPaginated } from '../fetcher';
import { AddressMasterData, MasterData } from './master-data';
import { MPPDetail } from './mpp';
import { FileData } from './types';

export const usePrintDisbursementTodoQuery = () => {
	return useQuery({
		queryKey: ['/disbursement-document-printing/get-things-todo'] as const,
		queryFn: ({ queryKey }) => {
			const [api] = queryKey;
			return fetcher<number>({
				api,
			});
		},
	});
};

export const usePrintDisbursementSearchOptionQuery = () => {
	return useQuery({
		queryKey: ['/disbursement-document-printing/mpp-search-options'] as const,
		queryFn: ({ queryKey }) => {
			const [api] = queryKey;
			return fetcher<{
				searchByOptions: MasterData[];
				divisionOptions: MasterData[];
				frmOptions: MasterData[];
				fbdhOptions: MasterData[];
			}>({
				api,
			});
		},
	});
};

export type PrintDisbursementSearchMPPParam = FleetGetListParams &
	Partial<{
		divisionId: number;
		frmId: number;
		fbdhId: number;
	}>;

export const usePrintDisbursementSearchMPP = (
	body: PrintDisbursementSearchMPPParam,
) => {
	return useQuery({
		queryKey: ['/disbursement-document-printing/search-mpp', body] as const,
		queryFn: ({ queryKey }) => {
			const [api] = queryKey;
			return fetcher<
				FleetPaginated<{
					id: number;
					no: number;
					mppId: number;
					mppNo: string;
					customerId: number;
					customerName: string;
					bmppGroupId: number;
					bmppGroupName: string;
					frm: string;
					fbdh: string;
					division: string;
					allowAction: boolean;
				}>
			>({
				api,
				body,
			});
		},
	});
};

export const usePrintDisbursementSearchPOOptionQuery = () => {
	return useQuery({
		queryKey: ['/disbursement-document-printing/po-search-options'] as const,
		queryFn: ({ queryKey }) => {
			const [api] = queryKey;
			return fetcher<{
				searchByOptions: MasterData[];
				purchaseOrderStatusOptions: MasterData[];
				fcohStatusOptions: MasterData[];
			}>({
				api,
			});
		},
	});
};

export type PrintDisbursementSearchPOOrderParam = FleetGetListParams & {
	mppId: number;
	purchaseOrderStatus?: string;
	applicationStatus?: string;
	fcohVerificationStatus?: string;
	purchaseOrderDate?: string;
};

export const usePrintDisbursementSearchPOOrderQuery = (
	body: PrintDisbursementSearchPOOrderParam,
) => {
	return useQuery({
		queryKey: [
			'/disbursement-document-printing/search-purchase-order',
			body,
		] as const,
		queryFn: ({ queryKey }) => {
			const [api] = queryKey;
			return fetcher<{
				mainInformation: {
					customerId: string;
					customerName: string;
					bmppGroupId: string;
					bmppGroupName: string;
					mppId: number;
					mppNo: string;
				};
				items: {
					simulationId: number;
					simulationNo: string;
					purchaseOrderId: number;
					purchaseOrderNo: string;
					financingProduct: MasterData;
					applicationId: string;
					agreementId: number;
					agreementNo: string;
					plakatNo: string;
					supplierName: string;
					picName: string;
					supplierPhone: string;
					asset: string;
					unit: number;
					ntf: number;
					purchaseOrderDate: string;
					purchaseOrderStatus: string;
					verificationStatus: string;
					applicationStatus: string;
					allowCheck: boolean;
				}[];
				ntfTotal: number;
				ntfInVerificationProcess: number;
				ntfVerified: number;
				ntfGoLive: number;
			}>({
				api,
				body,
			});
		},
	});
};

export const usePrintDisbursementVerificationDetailQuery = (body: {
	mppId: number;
	disbursementId?: number;
	financingProductId?: number;
	purchaseOrderIds?: number[];
}) => {
	return useQuery({
		queryKey: [
			'/disbursement-document-printing/get-verification-detail',
			body,
		] as const,
		queryFn: ({ queryKey }) => {
			const [api] = queryKey;
			return fetcher<{
				id: number;
				disbursementNo: string;
				status: number;
				documentSigningPicName: string;
				documentSigningPicIdCardNo: string;
				documentSigningPicPosition: string;
				documentSigningPicZipCode: AddressMasterData;
				documentSigningPicAddress: string;
				collectionPicName: string;
				collectionPicPosition: string;
				collectionPicPhoneNo: string;
				documents: {
					id: number;
					masterDocumentId: number;
					documentName: string;
					documentType: number;
					isMandatory: boolean;
					fileUrl: string;
					isChecked: boolean;
				}[];
				approvalActions: MasterData[];
				mppDetails: MPPDetail;
				fcdvsList: MasterData[];
				fcasList: MasterData[];
				fcasName: string;
				fcdvsName: string;
				fcdvsNotes: string;
				documentTemplates: {
					order: number;
					documentTypeName: string;
					documents: {
						id: number;
						no: number;
						name: string;
						slug: string;
					}[];
				}[];
				approvalHistory: {
					id: number;
					name: string;
					status: string;
					notes: string;
					process: string;
					date: string;
				}[];
			}>({
				api,
				body,
			});
		},
	});
};

export type PrintDisbursementSubmitVerificationPayload = {
	purchaseOrderIds: number[];
	fcdvsId: number;
	fcasId: number;
	action: string;
	notes: string;
	covenants: {
		id: number;
		statusId: string;
		additionalInformation: string;
	}[];
};

export const usePrintDisbursementSubmitVerificationMutation = () => {
	return useMutation({
		mutationFn: (body: PrintDisbursementSubmitVerificationPayload) => {
			return fetcher<boolean>({
				api: '/disbursement-document-printing/submit-request-verification',
				body,
			});
		},
	});
};

export type PrintDisbursementSubmitDocumentPayload = {
	mppId: number;
	documentSigningPicName: string;
	documentSigningPicIdCardNo: string;
	documentSigningPicPosition: string;
	documentSigningPicZipCode: number;
	documentSigningPicAddress: string;
	collectionPicName: string;
	collectionPicPhoneNo: string;
	collectionPicPosition: string;
	action: string;
	notes: string;
	purchaseOrderIds: number[];
};

export const usePrintDisbursementSubmitDocumentMutation = () => {
	return useMutation({
		mutationFn: (body: PrintDisbursementSubmitDocumentPayload) => {
			return fetcher<string>({
				api: '/disbursement-document-printing/submit-document',
				body,
			});
		},
	});
};

export const useViewPurchaseOrderHistoryQuery = (id: number) => {
	return useQuery({
		queryKey: [
			'/disbursement-document-printing/view-history-purchase-order',
			id,
		] as const,
		queryFn: ({ queryKey }) => {
			const [api, id] = queryKey;
			return fetcher<{
				mainInformation: {
					customerId: string;
					customerName: string;
					bmppGroupId: string;
					bmppGroupName: string;
					mppId: number;
					mppNo: string;
					mppDate: string;
				};
				items: {
					action: string;
					actionDate: string;
					simulationId: number;
					simulationNo: string;
					purchaseOrderId: number;
					purchaseOrderNo: string;
					financingProduct: {
						id: number;
						value: string;
					};
					applicationId: string;
					agreementId: number;
					agreementNo: string;
					plakatNo: string;
					supplierName: string;
					supplierPicName: string;
					supplierPhone: string;
					asset: string;
					unit: number;
					ntf: number;
					purchaseOrderDate: string;
					purchaseOrderStatus: string;
					verificationStatus: string;
					applicationStatus: string;
				}[];
				ntfTotal: number;
				ntfInVerificationProcess: number;
				ntfVerified: number;
				ntfGoLive: number;
				ntfNotYetRequstedTotal: number;
			}>({
				api,
				body: {
					id,
				},
			});
		},
	});
};

export const downloadDisbursementDocument = (body: {
	id: number;
	mppId: number;
}) => {
	return fetcher<{ signedUrl: string }>({
		api: '/disbursement-document-printing/download-document-template',
		body,
	});
};

export const useDownloadDocumentMutation = () => {
	return useMutation({
		mutationFn: downloadDisbursementDocument,
	});
};

export const useDownloadPODocument = () => {
	return useMutation({
		mutationFn: (purchaseOrderIds: number[]) => {
			return fetcher<FileData[]>({
				api: '/purchase-orders/download-document',
				body: {
					purchaseOrderIds,
				},
			});
		},
	});
};
